import React, { useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';

import { ContentLayoutContext } from '../../Contexts';
import useContentError from '../../hooks/useContentError';
import useContentLayout from '../../hooks/useContentLayout';
import { IContentLayoutProvider } from './types';

/**
 * Provides Content Layout context to consumers.
 *
 * @param props.children - React children
 * @param props.hasPanel - If your layout includes a Panel component then
 *  inform the context with this prop.
 * @param props.isDashboard - if layout should use the narrow dashboard layout.
 *  will override dashboardPaths.
 * @param props.dashboardPaths - if some but not all routes nested directly
 *  beneath the content layout need the dashboard layout
 *  pass the array of paths.
 */
const ContentLayoutProvider = ({
  children,
  dashboardPaths = [],
  hasPanel = false,
  isDashboard: isDashboardProp = false,
}: IContentLayoutProvider): JSX.Element => {
  const error = useContentError();
  const match = useRouteMatch({ path: dashboardPaths });
  const isDashboard = isDashboardProp || !!match;
  const contentLayout = useContentLayout({ hasPanel, isDashboard });
  const value = useMemo(
    () => ({ error, ...contentLayout }),
    [error, contentLayout],
  );
  return (
    <ContentLayoutContext.Provider value={value}>
      {children}
    </ContentLayoutContext.Provider>
  );
};

export default ContentLayoutProvider;
